/**
 * Shared viewport-aware dropdown positioning utility.
 *
 * Calculates fixed-position coordinates for portal-based dropdown menus,
 * flipping and clamping to keep the dropdown fully within the viewport.
 */

export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface DropdownPositionOptions {
  /** Preferred placement relative to trigger (default: 'bottom-end') */
  placement?: Placement;
  /** Estimated dropdown height in px — used for flip/clamp decisions */
  estimatedHeight?: number;
  /** Estimated dropdown width in px — used for flip/clamp decisions */
  estimatedWidth?: number;
  /** Min distance from viewport edges in px (default: 8) */
  padding?: number;
  /** Gap between trigger and dropdown in px (default: 4) */
  offset?: number;
}

export interface DropdownPosition {
  top: number;
  left: number;
  /** Actual vertical placement after flip logic ('bottom' | 'top') */
  placement: 'bottom' | 'top';
}

const DEFAULTS: Required<DropdownPositionOptions> = {
  placement: 'bottom-end',
  estimatedHeight: 240,
  estimatedWidth: 160,
  padding: 8,
  offset: 4,
};

/**
 * Calculate viewport-aware fixed position for a dropdown menu.
 *
 * Call this imperatively in an onClick/onToggle handler:
 * ```ts
 * const pos = calculateDropdownPosition(e.currentTarget, { estimatedHeight: 300, estimatedWidth: 160 });
 * setMenuPosition(pos);
 * ```
 */
export function calculateDropdownPosition(trigger: HTMLElement, options?: DropdownPositionOptions): DropdownPosition {
  const opts = { ...DEFAULTS, ...options };
  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // --- Vertical placement ---
  const preferBottom = opts.placement.startsWith('bottom');
  const spaceBelow = vh - rect.bottom - opts.padding;
  const spaceAbove = rect.top - opts.padding;

  let placeBottom: boolean;
  if (preferBottom) {
    placeBottom = spaceBelow >= opts.estimatedHeight + opts.offset || spaceBelow >= spaceAbove;
  } else {
    placeBottom = spaceAbove < opts.estimatedHeight + opts.offset && spaceBelow > spaceAbove;
  }

  let top: number;
  if (placeBottom) {
    top = rect.bottom + opts.offset;
    // Clamp: if it still overflows bottom, push it up
    if (top + opts.estimatedHeight > vh - opts.padding) {
      top = vh - opts.padding - opts.estimatedHeight;
    }
  } else {
    top = rect.top - opts.offset - opts.estimatedHeight;
    // Clamp: if it still overflows top, push it down
    if (top < opts.padding) {
      top = opts.padding;
    }
  }

  // --- Horizontal placement ---
  const preferEnd = opts.placement.endsWith('end');
  let left: number;
  if (preferEnd) {
    // Align dropdown's right edge with trigger's right edge
    left = rect.right - opts.estimatedWidth;
  } else {
    // Align dropdown's left edge with trigger's left edge
    left = rect.left;
  }

  // Clamp horizontally
  if (left + opts.estimatedWidth > vw - opts.padding) {
    left = vw - opts.padding - opts.estimatedWidth;
  }
  if (left < opts.padding) {
    left = opts.padding;
  }

  return { top, left, placement: placeBottom ? 'bottom' : 'top' };
}
